import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  getPendingMandatoryNotifications,
  markNotificationsRead,
} from "../../../api/system/notifications";
import { socket } from "../../../utils/socket";

/**
 * Thông báo từ admin (mandatory_modal): chặn giao diện, đếm ngược tối thiểu,
 * sau đó tự đóng và đánh dấu đã đọc (hoặc bấm nút).
 */
export default function AdminMandatoryNotifications() {
  const [items, setItems] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const processingRef = useRef(false);

  const load = useCallback(async () => {
    const list = await getPendingMandatoryNotifications();
    setItems(Array.isArray(list) ? list : []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onNew = () => load();
    socket.on("notification:new", onNew);
    return () => socket.off("notification:new", onNew);
  }, [load]);

  const current = items[0];

  useEffect(() => {
    if (!current) {
      setSecondsLeft(0);
      return;
    }
    const min = Math.max(3, Math.min(120, Number(current.min_display_seconds) || 10));
    setSecondsLeft(min);
    const tick = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [current?.id]);

  const finishOne = useCallback(
    async (notificationId) => {
      if (!notificationId || processingRef.current) return;
      processingRef.current = true;
      setSubmitting(true);
      try {
        await markNotificationsRead([notificationId]);
        window.dispatchEvent(new Event("refresh-notification-count"));
        await load();
        window.dispatchEvent(new Event("mandatory-queue-updated"));
      } catch (e) {
        console.error("AdminMandatoryNotifications finishOne:", e);
      } finally {
        processingRef.current = false;
        setSubmitting(false);
      }
    },
    [load]
  );

  useEffect(() => {
    if (!current?.id || secondsLeft > 0) return;
    const t = setTimeout(() => finishOne(current.id), 500);
    return () => clearTimeout(t);
  }, [secondsLeft, current?.id, finishOne]);

  if (!current) return null;

  return (
    <AnimatePresence>
      <motion.div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="admin-mandatory-title"
        className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
          initial={{ scale: 0.96, y: 16 }}
          animate={{ scale: 1, y: 0 }}
        >
          <div className="px-6 py-4 bg-gradient-to-r from-red-700 to-red-600 text-white shrink-0">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-90">
              Thông báo từ Ban quản trị
            </p>
            <h2 id="admin-mandatory-title" className="text-lg font-bold mt-1 leading-snug">
              {current.title}
            </h2>
          </div>
          <div className="px-6 py-4 overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap flex-1 min-h-[120px]">
            {current.message || "—"}
          </div>
          <div className="px-6 py-4 border-t bg-gray-50 flex flex-col gap-2 shrink-0">
            {secondsLeft > 0 ? (
              <p className="text-center text-xs text-gray-500">
                Vui lòng đọc nội dung — đóng sau{" "}
                <span className="font-bold text-red-600 tabular-nums">{secondsLeft}s</span>
              </p>
            ) : (
              <p className="text-center text-xs text-green-700 font-medium">
                {submitting ? "Đang xử lý…" : "Đang đóng…"}
              </p>
            )}
            <button
              type="button"
              disabled={secondsLeft > 0 || submitting}
              onClick={() => finishOne(current.id)}
              className="w-full py-3 rounded-xl bg-red-600 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
            >
              Đã đọc và đóng
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
